import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Search, BookOpen, Video, FileText, MessageCircle, Mail } from 'lucide-react';

export default function HelpPage(): React.ReactElement {
  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-primary" />
              Help & Support
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Access documentation, tutorials, and support resources
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-6 p-6">

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search for help articles, guides, and FAQs..."
          className="pl-10 h-12 text-lg"
        />
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
          <CardHeader>
            <BookOpen className="h-8 w-8 mb-2 text-primary" />
            <CardTitle>User Guide</CardTitle>
            <CardDescription>Comprehensive documentation and tutorials</CardDescription>
          </CardHeader>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
          <CardHeader>
            <Video className="h-8 w-8 mb-2 text-primary" />
            <CardTitle>Video Tutorials</CardTitle>
            <CardDescription>Step-by-step video walkthroughs</CardDescription>
          </CardHeader>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
          <CardHeader>
            <FileText className="h-8 w-8 mb-2 text-primary" />
            <CardTitle>Release Notes</CardTitle>
            <CardDescription>Latest features and updates</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* FAQs */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
          <CardDescription>Common questions and answers</CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How do I add a new asset?</AccordionTrigger>
              <AccordionContent>
                Navigate to the Assets page and click the "Add Asset" button. Fill in the required fields including Asset ID, Name, Category, Location, and Acquisition Cost. You can also bulk import assets using the Import feature.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>How does the depreciation workflow work?</AccordionTrigger>
              <AccordionContent>
                The depreciation workflow follows five steps: Prepare (select assets and period), Validate (check calculations), Review (verify journal entries), Approve (obtain authorisation), and Post (commit to ledger). Each step must be completed before proceeding to the next.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>Can I export asset data?</AccordionTrigger>
              <AccordionContent>
                Yes, you can export asset data from multiple locations. On the Assets page, use the Export button to download data in Excel, PDF, or CSV format. The Enquiry page also provides export functionality for transaction data.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>How do I track asset insurance and warranties?</AccordionTrigger>
              <AccordionContent>
                When editing an asset, navigate to the "Insurance & Warranty" tab. Here you can record policy details, expiry dates, and attach relevant documents. The Dashboard will alert you to upcoming renewals.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>What are saved views and how do I use them?</AccordionTrigger>
              <AccordionContent>
                Saved views allow you to save your filter and column preferences for quick access. Apply your desired filters and column settings, then click "Saved Views" and select "Save Current View". You can access your saved views anytime from the same menu.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>How do I revalue an asset?</AccordionTrigger>
              <AccordionContent>
                Navigate to Transactions → Revaluation (Bulk). Create a new revaluation batch, add the assets you want to revalue, enter the new values, and follow the workflow through validation, review, approval, and posting.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger>Can I link related assets together?</AccordionTrigger>
              <AccordionContent>
                Yes, in the asset editor, go to the "Suppliers & Linked Equipment" tab. Click "Link Asset" to associate related or dependent assets. This is useful for tracking equipment sets or parent-child asset relationships.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8">
              <AccordionTrigger>How do I view the audit trail for an asset?</AccordionTrigger>
              <AccordionContent>
                Open the asset editor and navigate to the "Attachments & Audit" tab. The audit trail shows a complete history of all changes made to the asset, including who made the change, when, and what was modified.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card>
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
          <CardDescription>Get in touch with our support team</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button variant="outline" className="h-auto py-4 justify-start">
              <MessageCircle className="mr-3 h-5 w-5" />
              <div className="text-left">
                <div className="font-semibold">Live Chat</div>
                <div className="text-sm text-muted-foreground">Chat with our support team</div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto py-4 justify-start">
              <Mail className="mr-3 h-5 w-5" />
              <div className="text-left">
                <div className="font-semibold">Email Support</div>
                <div className="text-sm text-muted-foreground">support@assetmanagement.com</div>
              </div>
            </Button>
          </div>

          <div className="rounded-lg bg-muted p-4">
            <div className="font-semibold mb-2">Support Hours</div>
            <div className="text-sm text-muted-foreground">
              Monday - Friday: 9:00 AM - 6:00 PM GMT<br />
              Saturday - Sunday: Closed<br />
              Response time: Within 24 hours
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
